import { useState } from "react";
import { useTranslation, Trans } from "next-i18next";
import router from "next/router";

import { Modal } from "common/ui/Modal";
import { Stack } from "common/ui/Stack";
import { Skeleton } from "common/ui/Skeleton";
import { Paragraph } from "common/ui/Paragraph";
import { Heading } from "common/ui/Heading";

import { useSharedOpportunity } from "common/api/getSharedOpportunity";
import { acceptRequestToChat } from "../../api/acceptRequestToChat";
import { declineRequestToChat } from "../../api/declineRequestToChat";

interface Props {
  close: () => void,
  opportunityId: string,
  message: string,
  senderName: string,
  onSuccess?: () => Promise<void>,
}

export const ChatRequestResponseModal = ({
  close,
  opportunityId,
  message,
  senderName,
  onSuccess,
}: Props) => {
  const { opportunity, isOpportunityLoading } = useSharedOpportunity(opportunityId);
  const { t } = useTranslation("common");

  const [isProcessing, setIsProcessing] = useState(false);
  const [err, setErr] = useState("");

  const handleAccept = async () => {
    setIsProcessing(true);

    try {
      await acceptRequestToChat(opportunityId);
      await onSuccess();
      router.push(`/chat?channelUrl=${opportunityId}`);
      close();
    } catch (err) {
      setErr(t("chatRequestResponseModal.somethingWentWrong"));
    }

    setIsProcessing(false);
  }

  const handleDecline = async () => {
    setIsProcessing(true);

    try {
      await declineRequestToChat(opportunityId);
      await onSuccess();
      close();
    } catch (err) {
      setErr(t("chatRequestResponseModal.somethingWentWrong"));
    }

    setIsProcessing(false);
  }

  return (
    <Modal
      close={close}
      title={t("chatRequestResponseModal.request")}
      primaryAction={{
        text: t("common.accept"),
        onClick: handleAccept,
      }}
      secondaryAction={{
        text: t("common.decline"),
        onClick: handleDecline,
      }}
      isProcessing={isProcessing}
      error={err}
    >
      <Stack direction="column" gap={24}>
        {isOpportunityLoading ? (
          <>
            <Skeleton type="p1" width={160} />
            <Stack direction="column" gap={4}>
              {[...Array(2)].map((i, idx) => (
                <Skeleton key={idx} type="p1" width={200} />
              ))}
            </Stack>
          </>
        ) : (
          <>
            <Paragraph variant="p1">
              <Trans
                t={t}
                i18nKey="chatRequestResponseModal.sender"
                values={{
                  sender: senderName,
                  address: opportunity?.listing?.fullAddress,
                }}
                components={{ bold: <b /> }}
              />
            </Paragraph>
            <Stack direction="column" gap={4}>
              <Heading variant="h4">
                {t("common.message")}
              </Heading>
              <Paragraph variant="p1">
                {message}
              </Paragraph>
            </Stack>
          </>
        )}
      </Stack>
    </Modal>
  )
}
